import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  qualityPenaltyFromRejections,
  rbmCalculateTimePenalty,
} from "./rbmCalculations";

const ConfirmSubmission = (props) => {
  const {
    submissions,
    targetShare,
    measurementUnitCode = "SUM",
    reportDeadline,
    formData,
    activityWeight,
    reportingMode,
    selectedMonth = null,
    measurementUnit,
    onClose,
    onSubmit,
    disabled,
  } = props;

  const [previewScore, setPreviewScore] = useState(null);

  const previewTints = useMemo(() => {
    if (!previewScore) {
      return {
        isZero: false,
        isLow: false,
        scoreColor: "inherit",
        barColor: "primary.main",
        trackColor: "grey.200",
        leftEmphasisSx: {},
      };
    }
    const exp = Number(previewScore?.expected ?? 0);
    const isZero = exp === 0;
    const isLow = !isZero && exp < 50;

    const scoreColor = isZero
      ? "error.main"
      : isLow
      ? "warning.dark"
      : "inherit";
    const barColor = isZero
      ? "error.main"
      : isLow
      ? "warning.main"
      : "primary.main";
    const trackColor = isZero
      ? "error.light"
      : isLow
      ? "warning.light"
      : "grey.200";

    return {
      isZero,
      isLow,
      scoreColor,
      barColor,
      trackColor,
      leftEmphasisSx: { color: scoreColor, fontWeight: 700 },
    };
  }, [previewScore?.expected]);

  const computePreview = () => {
    const assumedQualityValue = 95; // temporary quality preview

    const rejections = (submissions || [])
      .filter((s) => s?.evaluation && s.evaluation.accepted === false)
      .map((s) => ({ createdOn: s.evaluation.createdOn }));

    const timePenalty = rbmCalculateTimePenalty({
      deadline: reportDeadline,
      submissions,
      rejections,
    });

    const qualityPenalty = qualityPenaltyFromRejections({
      rejectionsCount: (rejections || []).length,
    });

    const target = Number(targetShare || 0);
    const achieved = Number(formData.achievedTarget || 0);
    const measurementCode = measurementUnitCode;
    const weight = Number(activityWeight || 1);
    // const totalWeight = Number(totalWeight || 1);

    // Compute ratio based on measurement unit type
    let ratio = 0;

    switch (measurementCode) {
      case "%DOWN":
        // lower is better
        if (target <= 0) ratio = 1;
        else if (achieved <= target)
          ratio = 1; // achieved less than or equal to target = perfect
        else ratio = Math.max(0, 1 - (achieved - target) / 100);
        break;

      case "%UP":
      case "SUM":
      case "REPEAT":
      case "FIXED":
        ratio = target > 0 ? achieved / target : 1;
        break;

      default:
        ratio = target > 0 ? achieved / target : 1;
        break;
    }

    // Clamp between 0–1
    ratio = Math.min(1, Math.max(0, ratio));

    // Base and penalty
    const baseScore = ratio * 100;
    const weightPenalty = (1 - ratio) * weight;
    let weightedScore = baseScore - weightPenalty;
    if (weightedScore < 0) weightedScore = 0;

    // Apply quality AFTER penalty weighting
    const qualityAdjusted = weightedScore * (assumedQualityValue / 100);

    // Combine all penalties
    const totalPenalty = Number(timePenalty || 0) + Number(qualityPenalty || 0);

    // Expected final score after all penalties
    const expected = Math.max(0, Math.min(100, qualityAdjusted - totalPenalty));

    setPreviewScore({
      fields: {
        reportingMode,
        monthLabel: selectedMonth?.label || null,
        target,
        achievedShown: achieved,
        unit: measurementUnit,
        completed: formData.completed || "—",
        attachmentName: formData.file?.name || null,
      },
      baseScore,
      ratio,
      weightPenalty,
      weightedScore,
      qualityAdjusted,
      quality: assumedQualityValue,
      weight: weight,
      penalties: { timePenalty, qualityPenalty, totalPenalty },
      expected,
    });
  };

  useEffect(() => {
    computePreview();
  }, []);

  if (!previewScore) return null;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Confirm submission</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* LEFT: Reporting summary */}
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" aria-label="submission summary">
                <TableBody>
                  <TableRow>
                    <TableCell width="45%">
                      <strong>Reporting mode</strong>
                    </TableCell>
                    <TableCell>
                      {String(
                        previewScore.fields.reportingMode || ""
                      ).toUpperCase()}
                      {previewScore.fields.reportingMode === "monthly" &&
                        previewScore.fields.monthLabel && (
                          <Chip
                            className="ml-2"
                            size="small"
                            label={previewScore.fields.monthLabel}
                          />
                        )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <strong>Target</strong>
                    </TableCell>
                    <TableCell>
                      {previewScore.fields.target}{" "}
                      {previewScore.fields.unit || ""}
                    </TableCell>
                  </TableRow>

                  {targetShare > 0 ? (
                    <TableRow>
                      <TableCell>
                        <strong>Achieved</strong>
                      </TableCell>
                      <TableCell sx={previewTints.leftEmphasisSx}>
                        {previewScore.fields.achievedShown}{" "}
                        {previewScore.fields.unit || ""}
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell>
                        <strong>Completed</strong>
                      </TableCell>
                      <TableCell sx={previewTints.leftEmphasisSx}>
                        {previewScore.fields.completed}
                        {previewScore.fields.completed === "no" && (
                          <Chip
                            className="ml-2"
                            size="small"
                            color={previewTints.isZero ? "error" : "warning"}
                            variant="outlined"
                            label={`Progress: ${
                              previewScore.fields.achievedShown || 0
                            }%`}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow>
                    <TableCell>
                      <strong>Attachment</strong>
                    </TableCell>
                    <TableCell>
                      {previewScore.fields.attachmentName ? (
                        <Chip
                          size="small"
                          color="success"
                          label={previewScore.fields.attachmentName}
                        />
                      ) : (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label="No file attached"
                        />
                      )}
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <strong>Assumed quality (set by supervisor)</strong>
                    </TableCell>
                    <TableCell>{previewScore.quality}%</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>
                      <strong>Weight</strong>
                    </TableCell>
                    <TableCell>{previewScore.weight}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {!previewScore.fields.attachmentName && (
              <Alert severity="warning" variant="outlined" className="mt-2">
                No supporting document attached.
              </Alert>
            )}
          </Grid>

          {/* RIGHT: Score breakdown */}
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 1.5, height: "100%" }}
            >
              <Stack spacing={1.25}>
                <Typography variant="subtitle2" className="text-uppercase">
                  Score breakdown
                </Typography>

                <Stack direction="row" justifyContent="space-between">
                  <span>Base score</span>
                  <Box
                    component="strong"
                    sx={{ color: previewTints.scoreColor }}
                  >
                    {previewScore.baseScore.toFixed(2)}%
                  </Box>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <span>Weighted score</span>
                  <Box
                    component="strong"
                    sx={{ color: previewTints.scoreColor }}
                  >
                    {previewScore.weightedScore.toFixed(2)}%
                  </Box>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <span>Report quality applied</span>
                  <Box
                    component="strong"
                    sx={{ color: previewTints.scoreColor }}
                  >
                    {previewScore.qualityAdjusted.toFixed(2)}%
                  </Box>
                </Stack>

                <Divider />

                <Stack direction="row" justifyContent="space-between">
                  <span>Time penalty</span>
                  <Box
                    component="strong"
                    sx={{ color: previewTints.scoreColor }}
                  >
                    {!!previewScore.penalties.timePenalty ? "-" : null}
                    {previewScore.penalties.timePenalty}
                  </Box>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <span>Quality penalty</span>
                  <Box
                    component="strong"
                    sx={{ color: previewTints.scoreColor }}
                  >
                    {!!previewScore.penalties.qualityPenalty ? "-" : null}
                    {previewScore.penalties.qualityPenalty}
                  </Box>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <span>Total penalties</span>
                  <Box
                    component="strong"
                    sx={{ color: previewTints.scoreColor }}
                  >
                    {!!previewScore.penalties.totalPenalty ? "-" : null}
                    {previewScore.penalties.totalPenalty}
                  </Box>
                </Stack>

                <Divider />

                <Typography
                  variant="body2"
                  sx={{ color: previewTints.scoreColor }}
                >
                  Expected max score
                </Typography>

                <div className="d-flex align-items-center">
                  <div style={{ flex: 1, marginRight: 8 }}>
                    <LinearProgress
                      variant="determinate"
                      value={previewScore.expected}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: previewTints.trackColor,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: previewTints.barColor,
                        },
                      }}
                    />
                  </div>
                  <Box
                    component="strong"
                    sx={{
                      minWidth: 72,
                      textAlign: "right",
                      color: previewTints.scoreColor,
                    }}
                  >
                    {previewScore.expected.toFixed(2)}%
                  </Box>
                </div>

                {targetShare === 0 &&
                  previewScore.fields.completed === "no" &&
                  Number(previewScore.fields.achievedShown || 0) === 0 && (
                    <Alert
                      severity={previewTints.isZero ? "error" : "warning"}
                      variant="outlined"
                    >
                      Zero progress will score 0%.
                    </Alert>
                  )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className="d-flex align-items-center justify-content-center py-2">
        <Button onClick={onClose} className="mr-2" disabled={disabled}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={!onSubmit || disabled}
        >
          Confirm & Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmSubmission;
